import { useState, useMemo } from 'react';
import { Plus, Pencil, ChevronDown, ChevronUp, FolderOpen, Users, X, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { cn } from '@/shared/lib/utils';
import { useProjects, useProjectMutations, useEmployeeAllocationMutations } from '../hooks/useProjects';
import { useEmployees } from '../hooks/useEmployees';
import { useAuth } from '@/shared/contexts/AuthContext';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';
import type { Project, Activity, EmployeeProjectAllocation } from '../types';

// ─────────────────────────────────────────────
// ActivityRow
// ─────────────────────────────────────────────
function ActivityRow({
  activity,
  isGestor,
  onEdit,
}: {
  activity: Activity;
  isGestor: boolean;
  onEdit: (a: Activity) => void;
}) {
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/30 transition-colors">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'inline-block h-2 w-2 rounded-full',
            activity.isActive ? 'bg-green-500' : 'bg-muted-foreground'
          )}
        />
        <span className="text-sm">{activity.name}</span>
        {activity.description && (
          <span className="text-xs text-muted-foreground">— {activity.description}</span>
        )}
      </div>
      {isGestor && (
        <button
          onClick={() => onEdit(activity)}
          className="p-1 rounded hover:bg-accent transition-colors text-muted-foreground"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// EmployeeAllocationSection
// ─────────────────────────────────────────────
function EmployeeAllocationSection({
  project,
  isGestor,
}: {
  project: Project;
  isGestor: boolean;
}) {
  const { data: allEmployees = [] } = useEmployees();
  const { allocate, deallocate } = useEmployeeAllocationMutations();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  const allocations: EmployeeProjectAllocation[] = project.employeeAllocations ?? [];

  // Employees already allocated (active)
  const allocatedIds = new Set(allocations.map((a) => a.employeeId));

  // Employees available to allocate — only COLLABORATORs
  const available = useMemo(
    () => allEmployees.filter((e) => e.user?.role === 'COLLABORATOR' && !allocatedIds.has(e.id)),
    [allEmployees, allocatedIds]
  );

  const handleAllocate = () => {
    if (!selectedEmployeeId) return;
    allocate.mutate({ employeeId: selectedEmployeeId, projectId: project.id });
    setSelectedEmployeeId('');
  };

  const handleDeallocate = (employeeId: string) => {
    deallocate.mutate({ projectId: project.id, employeeId });
  };

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
          <Users className="h-3.5 w-3.5" />
          Colaboradores
        </p>
        <span className="text-xs text-muted-foreground">{allocations.length} alocado{allocations.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Allocated list */}
      {allocations.length === 0 ? (
        <p className="text-xs text-muted-foreground py-1 px-3">Nenhum colaborador alocado.</p>
      ) : (
        <div className="space-y-1 mb-3">
          {allocations.map((alloc) => {
            const name = alloc.employee?.user?.name ?? alloc.employeeId;
            const email = alloc.employee?.user?.email;
            return (
              <div
                key={alloc.id}
                className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-muted/30"
              >
                <div>
                  <span className="text-sm font-medium">{name}</span>
                  {email && <span className="text-xs text-muted-foreground ml-2">{email}</span>}
                </div>
                {isGestor && (
                  <button
                    onClick={() => handleDeallocate(alloc.employeeId)}
                    disabled={deallocate.isPending}
                    className="p-1 rounded hover:bg-destructive/10 hover:text-destructive transition-colors text-muted-foreground disabled:opacity-50"
                    title="Remover alocação"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add allocation — gestor only */}
      {isGestor && available.length > 0 && (
        <div className="flex items-center gap-2">
          <select
            value={selectedEmployeeId}
            onChange={(e) => setSelectedEmployeeId(e.target.value)}
            className="flex-1 rounded-lg border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Selecionar colaborador...</option>
            {available.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.user?.name ?? emp.id}
              </option>
            ))}
          </select>
          <button
            onClick={handleAllocate}
            disabled={!selectedEmployeeId || allocate.isPending}
            className="flex items-center gap-1 rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-xs font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            <Plus className="h-3.5 w-3.5" />
            Alocar
          </button>
        </div>
      )}

      {isGestor && available.length === 0 && allEmployees.length > 0 && (
        <p className="text-xs text-muted-foreground px-3">Todos os colaboradores já estão alocados.</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// ProjectCard
// ─────────────────────────────────────────────
function ProjectCard({
  project,
  isGestor,
  onEditProject,
  onDeleteProject,
  onEditActivity,
  onAddActivity,
}: {
  project: Project;
  isGestor: boolean;
  onEditProject: (p: Project) => void;
  onDeleteProject: (p: Project) => void;
  onEditActivity: (a: Activity) => void;
  onAddActivity: (projectId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderOpen className={cn('h-5 w-5', project.isActive ? 'text-primary' : 'text-muted-foreground')} />
            <div>
              <CardTitle className="text-base">{project.name}</CardTitle>
              {project.description && (
                <p className="text-xs text-muted-foreground mt-0.5">{project.description}</p>
              )}
            </div>
            {!project.isActive && (
              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Inativo</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {isGestor && (
              <>
                <button
                  onClick={() => onEditProject(project)}
                  className="p-1.5 rounded hover:bg-accent transition-colors text-muted-foreground"
                  title="Editar projeto"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDeleteProject(project)}
                  className="p-1.5 rounded hover:bg-destructive/10 hover:text-destructive transition-colors text-muted-foreground"
                  title="Excluir projeto"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1.5 rounded hover:bg-accent transition-colors text-muted-foreground"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0">
          {/* Activities section */}
          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Atividades</p>
              {isGestor && (
                <button
                  onClick={() => onAddActivity(project.id)}
                  className="flex items-center gap-1 text-xs text-primary hover:opacity-80 transition-opacity"
                >
                  <Plus className="h-3 w-3" />
                  Adicionar
                </button>
              )}
            </div>
            {!project.activities || project.activities.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">Nenhuma atividade cadastrada.</p>
            ) : (
              <div>
                {project.activities.map((activity) => (
                  <ActivityRow
                    key={activity.id}
                    activity={activity}
                    isGestor={isGestor}
                    onEdit={onEditActivity}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Employee allocation section — gestor/admin can manage, others can view */}
          {isGestor && (
            <EmployeeAllocationSection project={project} isGestor={isGestor} />
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────
// ProjectModal
// ─────────────────────────────────────────────
type ModalMode =
  | { type: 'createProject' }
  | { type: 'editProject'; project: Project }
  | { type: 'createActivity'; projectId: string }
  | { type: 'editActivity'; activity: Activity }
  | null;

function ProjectModal({ mode, onClose }: { mode: Exclude<ModalMode, null>; onClose: () => void }) {
  const { createProject, updateProject, createActivity, updateActivity } = useProjectMutations();

  const [name, setName] = useState(
    mode.type === 'editProject' ? mode.project.name : mode.type === 'editActivity' ? mode.activity.name : ''
  );
  const [description, setDescription] = useState(
    mode.type === 'editProject'
      ? mode.project.description ?? ''
      : mode.type === 'editActivity'
        ? mode.activity.description ?? ''
        : ''
  );
  const [isActive, setIsActive] = useState(
    mode.type === 'editProject' ? mode.project.isActive : mode.type === 'editActivity' ? mode.activity.isActive : true
  );

  const title =
    mode.type === 'createProject'
      ? 'Novo Projeto'
      : mode.type === 'editProject'
        ? 'Editar Projeto'
        : mode.type === 'createActivity'
          ? 'Nova Atividade'
          : 'Editar Atividade';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode.type === 'createProject') {
      await createProject.mutateAsync({ name, description: description || undefined });
    } else if (mode.type === 'editProject') {
      await updateProject.mutateAsync({ id: mode.project.id, name, description: description || undefined, isActive });
    } else if (mode.type === 'createActivity') {
      await createActivity.mutateAsync({ projectId: mode.projectId, name, description: description || undefined });
    } else {
      await updateActivity.mutateAsync({ id: mode.activity.id, name, description: description || undefined, isActive });
    }
    onClose();
  };

  const isPending = createProject.isPending || updateProject.isPending || createActivity.isPending || updateActivity.isPending;
  const inputClass = 'w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl border w-full max-w-md">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">{title}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nome *</label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Descrição</label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={inputClass}
              />
            </div>
            {(mode.type === 'editProject' || mode.type === 'editActivity') && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="rounded"
                />
                Ativo
              </label>
            )}
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={isPending}
                className="flex-1 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {isPending ? 'Salvando...' : 'Salvar'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-medium rounded-lg border hover:bg-accent transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// ProjetosPage
// ─────────────────────────────────────────────
export default function ProjetosPage() {
  const { user } = useAuth();
  const isGestor = user?.role === 'GESTOR' || user?.role === 'ADMIN';
  const { data: projects, isLoading } = useProjects(true);
  const { deleteProject } = useProjectMutations();
  const [modal, setModal] = useState<ModalMode>(null);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

  const handleConfirmDelete = () => {
    if (projectToDelete) {
      deleteProject.mutate(projectToDelete.id);
      setProjectToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Projetos e Atividades</h1>
          <p className="text-muted-foreground mt-1">Gerencie projetos, atividades e colaboradores alocados</p>
        </div>
        {isGestor && (
          <button
            onClick={() => setModal({ type: 'createProject' })}
            className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="h-4 w-4" />
            Novo Projeto
          </button>
        )}
      </div>

      {modal && (
        <ProjectModal mode={modal} onClose={() => setModal(null)} />
      )}

      <ConfirmDialog
        open={!!projectToDelete}
        onOpenChange={(open) => !open && setProjectToDelete(null)}
        title="Excluir Projeto"
        description={`Tem certeza que deseja excluir o projeto "${projectToDelete?.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        icon="delete"
        isLoading={deleteProject.isPending}
      />

      {isLoading ? (
        <div className="space-y-4 animate-pulse">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-muted rounded-lg" />
          ))}
        </div>
      ) : !projects || projects.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Nenhum projeto cadastrado.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              isGestor={isGestor}
              onEditProject={(p) => setModal({ type: 'editProject', project: p })}
              onDeleteProject={(p) => setProjectToDelete(p)}
              onEditActivity={(a) => setModal({ type: 'editActivity', activity: a })}
              onAddActivity={(projectId) => setModal({ type: 'createActivity', projectId })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
